import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, animate } from "framer-motion";
import { Box, Flex, Image, Icon, IconButton, Text, useDisclosure, Button } from "@chakra-ui/react";
import ProjectModal from "./ProjectModal";

const FULL_WIDTH_PX = 100;
const COLLAPSED_WIDTH_PX = 50;
const GAP_PX = 4;
const MARGIN_PX = 5;

const projects = [
  {
    id: 1,
    title: "Wippe Track App + Website",
    subtitle: "Health Monitoring App & Website",
    description:
      "A health monitoring app for remote patient monitoring, featuring user-friendly interfaces for both app and website to optimize UI/UX and front-end development.",
    testimonial: "The user experience is exceptional, truly innovative!",
    featuredImage: "./projects-imgs/featured/featured-wippe.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-wippe.webp",
    detailedDescription:
      "Wippe Track is a health app developed for remote patient monitoring by BrHomMed company. It helps users control their health from a wide range of data sources, connected to devices integrated into the solution or also entered manually.\nAs an interface designer and web developer, my contribution to the Wippe Track project focused on creating a user-friendly interface and developing the interface of the app and website. I worked to optimise and ensure that the platform is not only functional but also easy to navigate, allowing users to monitor their health in a simple and intuitive way.",
    additionalImages: [
      "./projects-imgs/more-about/wippe-1.webp",
      "./projects-imgs/more-about/wippe-2.webp",
      "./projects-imgs/more-about/wippe-3.webp",
    ],
  },
  {
    id: 2,
    title: "Mateus App",
    subtitle: "Shopping App",
    description:
      "A shopping app offering exclusive promotions from Mateus stores, with an intuitive interface for easy access to various product categories.",
    testimonial: "Revolutionary approach to shopping accessibility!",
    featuredImage: "./projects-imgs/featured/featured-mateus.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-mateus.webp",
    detailedDescription:
      "Mateus Mais is a shopping app developed by Grupo Mateus S/A that offers exclusive promotions from the Mateus Supermercados, Mix Atacarejo, and Camiño chains, as well as news from the brands Bumba Meu Pão and Spazio. With the Mateus Mais app, users can make purchases online and opt for easy delivery of thousands of products from their stores or pick up at the store.\nAs Designer and Marketing Analyst, my contribution to the Mateus App project was on creating an intuitive and visually appealing interface for the app. I worked to deliver clear and intuitive access to offers from various categories such as grocery, cold cuts, meats, vegetables, drinks, hygiene and beauty products, stationery, bazaar, automotive products, products for their pet, bakery, specialties and much more.",
    additionalImages: [
      "./projects-imgs/more-about/mateus-1.webp",
      "./projects-imgs/more-about/mateus-2.webp",
      // "./projects-imgs/more-about/mateus-3.webp",
    ],
  },
  {
    id: 3,
    title: "Imoview CRM UI",
    subtitle: "Real Estate Management System",
    description:
      "A CRM system for real estate companies, providing user interface design, user journey mapping, and platform interface for effective business management.",
    testimonial: "Increased our sales efficiency by 300%!",
    featuredImage: "./projects-imgs/featured/featured-imoview.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-imoview.webp",
    detailedDescription:
      "Imoview CRM is real estate management system developed for Universal Software that allows Real state companies to manage their business bringing a good overview of their most relevant metrics to guide strategic decisions and planning.\nMy contribution to the project focused on user experience and interface design of the CRM and building digital marketing campaigns strategy, visual pieces and front development , ensuring to Imoview CRM an user-friendly and easy navigation and effective campaigns.",
    additionalImages: [
      "./projects-imgs/more-about/imoview-1.webp",
      // "./projects-imgs/more-about/imoview-2.webp",
      // "./projects-imgs/more-about/imoview-3.webp",
    ],
  },
  {
    id: 4,
    title: "Collab English Website",
    subtitle: "Educational Platform Website",
    description:
      "A website for an English school emphasizing student interaction, with intuitive layouts, responsive design, and front-end development.",
    testimonial: "The most engaging language learning experience ever!",
    featuredImage: "./projects-imgs/featured/featured-collab-english.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-collab-english.webp",
    detailedDescription:
      "Collab English is an English school in Brazil that prioritises student interaction and specific language learning strategies, even in remote classes.\nAs a UX/UI designer and front-end developer, my mission with Collab English was more than just create an intuitive website or a visual appealing brand, but translate the values, passion and vision of Junior into a meaningful identity. I focused on designing user-friendly layouts, implementing responsive design principles, and optimising for seamless interaction across any devices.\n\nwww.collabenglish.com.br",
    additionalImages: [
      "./projects-imgs/more-about/collab-1.webp",
      "./projects-imgs/more-about/collab-2.webp",
      "./projects-imgs/more-about/collab-3.webp",
    ],
  },
  {
    id: 5,
    title: "Vero Steel CRM UI",
    subtitle: "Energy Management CRM",
    description:
      "A CRM UI for optimizing energy usage in steel industry corporations, with simplified layout for clear statistics and control.",
    testimonial: "Completely transformed our energy management!",
    featuredImage: "./projects-imgs/featured/featured-vero.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-vero.webp",
    detailedDescription:
      "The Vero Steel project was developed by the information technology company Vetta Group. The purpose of the tool is to aid in the management of energy expenditure for steel industry corporations. The system assists with optimizing energy usage in a manner that decreases waste and reduces associated costs.\nI chose to prioritize functionality and clarity when designing the user interface and overall experience. My goal was to give facility managers a clear, uncomplicated view of important usage statistics and control configurations to help them implement energy efficiency measures. To achieve this, I used a simplified, intuitive layout for the platform that makes relevant information and tools easy to access and understand.",
    additionalImages: [
      "./projects-imgs/more-about/vero-1.webp",
      "./projects-imgs/more-about/vero-2.webp",
      // "./projects-imgs/more-about/vero-3.webp",
    ],
  },
  {
    id: 6,
    title: "Mapa Brand",
    subtitle: "Training Program Branding",
    description:
      "Brand guidelines, logo design, and marketing campaigns for a leadership training program using NLP and design thinking.",
    testimonial: "Motivating and inspiring branding for employee development!",
    featuredImage: "./projects-imgs/featured/featured-mapa.webp",
    backgroundImage: "./projects-imgs/backgrounds/bg-mapa.webp",
    detailedDescription:
      "Mapa was a training program I developed while working for Grupo Mateus in partnership with the Neuro-linguistic Programming Brazilian Society (SBPNL). The goal of Mapa was to provide professional education for Grupo Mateus employees, specifically targeting leadership skill development using Neuro-linguistic Programming techniques and Design Thinking principles.\n\nMy role in the project was to create the visual branding and marketing materials to promote awareness and engagement in the educational campaign across the company. This involved crafting a visual identity and overall aesthetic for Mapa that aligned with its objectives, as well as designing the promotional assets like posters and banners. The aim was to give employees a motivating and inspiring introduction to the training program as they developed as leaders.",
    additionalImages: [
      "./projects-imgs/more-about/mapa-1.webp",
      // "./projects-imgs/more-about/mapa-2.webp",
      // "./projects-imgs/more-about/mapa-3.webp",
    ],
  },
];

export default function AnimatedProjectsSlideshow() {
  const [index, setIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const x = useMotionValue(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!isDragging && containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth || 1;
        const targetX = -index * containerWidth;
        animate(x, targetX, {
          type: "spring",
          stiffness: 300,
          damping: 30,
        });
      }
    };

    updatePosition();

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [index, x, isDragging]);

  useEffect(() => {
    if (isPlaying && !isDragging) {
      const interval = setInterval(() => {
        setIndex((i) => (i + 1) % projects.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [isPlaying, isDragging]);

  const handleMoreInfo = (project) => {
    setSelectedProject(project);
    onOpen();
  };

  const toggleAutoplay = () => {
    setIsPlaying(!isPlaying);
  };

  const currentProject = projects[index];

  return (
    <>
      <Box maxW="100%" maxH="100%" w={"100%"} h={"100%"} mx="auto">
        <Flex direction="column" w={"100%"} zIndex={1} gap={3}>
          {/* Slideshow with only Project Backgrounds (no description content) */}
          <Box position="relative" overflow="hidden" ref={containerRef} zIndex={1}>
            <motion.div
              drag="x"
              dragElastic={0.2}
              dragMomentum={false}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={(e, info) => {
                setIsDragging(false);
                const containerWidth = containerRef.current?.offsetWidth || 1;
                const offset = info.offset.x;
                const velocity = info.velocity.x;
                let newIndex = index;
                if (Math.abs(velocity) > 500) {
                  newIndex = velocity > 0 ? index - 1 : index + 1;
                } else if (Math.abs(offset) > containerWidth * 0.3) {
                  newIndex = offset > 0 ? index - 1 : index + 1;
                }
                newIndex = Math.max(0, Math.min(projects.length - 1, newIndex));
                setIndex(newIndex);
              }}
              style={{ x, display: "flex" }}
            >
              {projects.map((project) => (
                // Project Background only — slides behind the Fixed Box
                <Box
                  key={project.id}
                  flexShrink={0}
                  w="full"
                  h="100vh"
                  backgroundImage={`url(${project.backgroundImage})`}
                  backgroundSize="cover"
                  backgroundPosition="center"
                  position="relative"
                  zIndex={1}
                >
                  {/* Dark overlay — stays on the background, visible through Fixed Box */}
                  <Box
                    position="absolute"
                    zIndex={2}
                    top={0}
                    left={0}
                    w="full"
                    h="full"
                    bg="black"
                    opacity={0.4}
                  />
                </Box>
              ))}
            </motion.div>
          </Box>

          {/* Fixed Box with border, backdrop filter, Thumbnails AND Project Description */}
          <Flex
            direction="column"
            border={"6px solid #F9F0DF"}
            backdropFilter={"blur(10px)"}
            background={"#00000020"}
            zIndex={2}
            borderRadius={"15px"}
            overflow={"hidden"}
            pos={"absolute"}
            bottom={{ base: "15px", md: "30px" }}
            left={{ base: "15px", md: "30px" }}
            w={{ base: "calc(100% - 30px)", md: "calc(100% - 60px)" }}
            h={{ base: "calc(100% - 100px)", md: "calc(100% - 120px)" }}
          >
            {/* Project Description — on top of the frosted glass, animated on index change */}
            <Flex
              flex="1"
              direction={{ base: "column-reverse", lg: "row" }}
              align="center"
              justify="center"
              gap={8}
              p={{ base: 14, md: 14 }}
              pointerEvents="all"
            >
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  style={{
                    display: "flex",
                    flex: 1,
                    flexDirection: "inherit",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "inherit",
                    width: "100%",
                  }}
                >
                  <Flex
                    direction="column"
                    maxW="500px"
                    h="fit-content"
                    margin={0}
                    align="flex-start"
                  >
                    <Text
                      as="h2"
                      fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                      fontWeight="bold"
                      color="white"
                      mb={2}
                      lineHeight="1.1"
                    >
                      {currentProject.title}
                    </Text>

                    <Text
                      as="h3"
                      fontSize={{ base: "lg", md: "xl" }}
                      color="white"
                      mb={4}
                      fontWeight="normal"
                    >
                      {currentProject.subtitle}
                    </Text>

                    <Text
                      fontSize={{ base: "md", lg: "lg" }}
                      color="whiteAlpha.900"
                      mb={6}
                      lineHeight="1.6"
                      textAlign="left"
                    >
                      {currentProject.description}
                    </Text>

                    <Button
                      onClick={() => handleMoreInfo(currentProject)}
                      _hover={{ transform: "scale(1.05)" }}
                      transition="transform 0.3s"
                      cursor="pointer"
                      mt={4}
                      color="white"
                      background="#DD9331"
                    >
                      More About {currentProject.title}
                    </Button>
                  </Flex>

                  <Flex
                    flex="1"
                    maxW={{ base: "250px", md: "280px", lg: "500px" }}
                    maxH="fit-content"
                    align="center"
                    justify="center"
                  >
                    <Box
                      onClick={() => handleMoreInfo(currentProject)}
                      _hover={{ transform: "scale(1.05)" }}
                      transition="transform 0.3s"
                      cursor="pointer"
                    >
                      <Image
                        src={currentProject.featuredImage}
                        alt={currentProject.title}
                        objectFit="contain"
                        w="100%"
                        h="100%"
                      />
                    </Box>
                  </Flex>
                </motion.div>
              </AnimatePresence>
            </Flex>

            {/* Bottom bar: Thumbnails */}
            <Thumbnails index={index} setIndex={setIndex} onMoreInfo={handleMoreInfo} />

            {/* Controls */}
            <Box
              pointerEvents={"all"}
              position="absolute"
              bottom={2}
              right={2}
              display={{ base: "none", md: "flex" }}
              gap={{ base: 2, md: 2 }}
              zIndex={3}
            >
              <IconButton
                icon={
                  isPlaying ? (
                    <Icon viewBox="0 0 24 24">
                      <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                    </Icon>
                  ) : (
                    <Icon viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </Icon>
                  )
                }
                onClick={toggleAutoplay}
                colorScheme="orange"
                padding="25px"
                size={{ base: "lg", md: "lg" }}
                aria-label={isPlaying ? "Pause autoplay" : "Start autoplay"}
                bg="#F9F0DF00"
                fill={"#F9F0DF"}
                border={"2px solid #F9F0DF"}
                _hover={{ bg: "#F9F0DF50" }}
              />

              <IconButton
                icon={
                  <Icon viewBox="0 0 24 24">
                    <path d="M15 19l-7-7 7-7" />
                  </Icon>
                }
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
                isDisabled={index === 0}
                color="#F9F0DF"
                padding="25px"
                size={{ base: "lg", md: "lg" }}
                aria-label="Previous project"
                bg="#F9F0DF00"
                fill={"#F9F0DF"}
                border={"2px solid #F9F0DF"}
                _hover={{ bg: "#F9F0DF50" }}
              />

              <IconButton
                icon={
                  <Icon viewBox="0 0 24 24">
                    <path d="M9 5l7 7-7 7" />
                  </Icon>
                }
                onClick={() => setIndex((i) => Math.min(projects.length - 1, i + 1))}
                isDisabled={index === projects.length - 1}
                padding="25px"
                size={{ base: "lg", md: "lg" }}
                aria-label="Next project"
                bg="#F9F0DF00"
                fill={"#F9F0DF"}
                border={"2px solid #F9F0DF"}
                _hover={{ bg: "#F9F0DF50" }}
              />
            </Box>
          </Flex>
        </Flex>
      </Box>
      <ProjectModal isOpen={isOpen} onClose={onClose} project={selectedProject} />
    </>
  );
}

function Thumbnails({ index, setIndex, onMoreInfo }) {
  const thumbnailsRef = useRef(null);
  useEffect(() => {
    if (thumbnailsRef.current) {
      let scrollPosition = 0;
      for (let i = 0; i < index; i++) {
        scrollPosition += COLLAPSED_WIDTH_PX + GAP_PX;
      }
      scrollPosition += MARGIN_PX;
      const containerWidth = thumbnailsRef.current.offsetWidth;
      const centerOffset = containerWidth / 2 - FULL_WIDTH_PX / 2;
      scrollPosition -= centerOffset;
      thumbnailsRef.current.scrollTo({
        left: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [index]);
  return (
    <Box
      ref={thumbnailsRef}
      overflowX="auto"
      sx={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      pos={"relative"}
      display={"flex"}
      justifyContent={{ base: "center", md: "start" }}
      w={"auto"}
      roundedTopEnd={{ base: "0px", md: "10px" }}
      alignSelf={"start"}
      zIndex={10}
      background={"#F9F0DF"}
      p={"5px 5px 0 0 "}
      pointerEvents={"all"}
    >
      <Flex gap={1} h="14" w="fit-content">
        {projects.map((project, i) => (
          <motion.button
            key={project.id}
            onClick={() => setIndex(i)}
            initial={false}
            animate={i === index ? "active" : "inactive"}
            variants={{
              active: {
                width: FULL_WIDTH_PX,
                marginLeft: MARGIN_PX,
                marginRight: MARGIN_PX,
              },
              inactive: {
                width: COLLAPSED_WIDTH_PX,
                marginLeft: 0,
                marginRight: 0,
              },
            }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            style={{
              position: "relative",
              flexShrink: 0,
              height: "100%",
              overflow: "hidden",
              borderRadius: "5px",
            }}
          >
            <Image
              src={project.backgroundImage}
              alt={project.title}
              w="full"
              h="full"
              objectFit="cover"
              pointerEvents="none"
              userSelect="none"
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                onMoreInfo(project);
              }}
            />
          </motion.button>
        ))}
      </Flex>
    </Box>
  );
}
